import React, { useEffect, useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { Button, Card, Col, Empty, Row, Select, Spin, Table, Tag } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeftOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useJobTitles } from "Hooks/AdminHooks/Organization/useJobTitles";

const JobTitles = () => {
  const {
    departments,
    loadingDepts,
    jobTitles,
    loadingJobs,
    fetchJobTitles,
    fetchDepartmentList,
    fetchCareerPaths,
    loadingCareerPaths,
    careerPaths,
  } = useJobTitles();

  const navigate = useNavigate();
  const location = useLocation();

  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedCareerPath, setSelectedCareerPath] = useState(null);

  // Initial Load
  useEffect(() => {
    fetchDepartmentList();
  }, []);

  // Handle Department Change
  const handleDepartmentChange = (value) => {
    setSelectedDepartment(value);
    setSelectedCareerPath(null); // Reset child selection
    fetchCareerPaths(value);     // Load paths for this dept
  };

  // Handle Career Path Change
  const handleCareerPathChange = (value) => {
    setSelectedCareerPath(value);
    fetchJobTitles(value); // Assuming fetchJobTitles now takes careerPathId
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "publicId",
      key: "publicId",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => {
        const maxLength = 50;
        if (!text) return "-";
        return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive) =>
        isActive ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => {
            navigate(`/edit-jobtitle/${record.id}`, {
              state: { departmentId: selectedDepartment, careerPathId: selectedCareerPath },
            });
          }}
        />
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div className="container-fluid py-4">
        {/* Header */}
        <Row className="mb-4" justify="space-between" align="middle">
          <Col>
            <h2 className="fw-bold mb-0">Job Titles Management</h2>
          </Col>
          <Col>
            <Button className="btn-back" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              Back
            </Button>
          </Col>
        </Row>

        {/* Selection Filters */}
        <Card>
          <Row gutter={[16, 16]} align="bottom">
            {/* 1. Department Dropdown */}
            <Col xs={24} md={8}>
              <label className="form-label ms-1">Department</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Select Department"
                loading={loadingDepts}
                onChange={handleDepartmentChange}
                value={selectedDepartment}
                options={departments?.map((dept) => ({
                  label: dept.name,
                  value: dept.id,
                }))}
              />
            </Col>

            {/* 2. Career Path Dropdown */}
            <Col xs={24} md={8}>
              <label className="form-label ms-1">Career Path</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Select Career Path"
                disabled={!selectedDepartment}
                loading={loadingCareerPaths}
                onChange={handleCareerPathChange}
                value={selectedCareerPath}
                options={careerPaths?.map((path) => ({
                  label: path.name,
                  value: path.id,
                }))}
              />
            </Col>

            {/* 3. Action Button */}
            <Col xs={24} md={8} className="d-flex align-items-end">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className="btn-primary w-100"
                disabled={!selectedCareerPath}
                onClick={() =>
                  navigate(`/create-jobtitle/${selectedCareerPath}`, {
                    state: { 
                      departmentId: selectedDepartment, 
                      careerPathId: selectedCareerPath 
                    },
                  })
                }
              >
                Add Job Title
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Data Table Area */}
        <div className="mt-4">
          {loadingJobs ? (
            <div className="text-center py-5">
              <Spin size="large" />
            </div>
          ) : selectedCareerPath ? (
            jobTitles && jobTitles.length > 0 ? (
              <Table 
                columns={columns} 
                dataSource={jobTitles} 
                rowKey="id" 
                pagination={{ pageSize: 10 }}
              />
            ) : (
              <Empty description="No Job Titles found for this Career Path" />
            )
          ) : (
            <Empty description="Please select both Department and Career Path to view Job Titles" />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default JobTitles;